'use client';

/**
 * The finished story itself, in the hero: a real published story of the
 * visitor's language, playable where the promise is made.
 *
 * The iframe is mounted only on the first press (a facade), because embedding
 * YouTube on load would pull its scripts and cookies for every visitor — also
 * those who declined tracking. Until then the poster is OUR cover art.
 *
 * The control is a pill in the corner, not a circle in the middle: the cover is
 * the argument, and a button across its face hides the very thing it advertises.
 */

import { useState } from 'react';
import Image from 'next/image';
import { Play } from 'lucide-react';

export function HeroStoryVideo({
  videoId,
  poster,
  playLabel,
  title,
  children,
}: {
  videoId: string;
  poster: string;
  /** e.g. "Смотреть сказку" */
  playLabel: string;
  /** The story's own title, shown next to the control. */
  title?: string;
  /** Captions drawn over the poster (the photo inset) — hidden once it plays. */
  children?: React.ReactNode;
}) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div className="relative aspect-video bg-black">
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
          title={title ?? playLabel}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="hero-story-iframe"
        />
      </div>
    );
  }

  return (
    <div className="relative aspect-video">
      <Image
        src={poster}
        alt=""
        fill
        priority
        sizes="(max-width: 1024px) 90vw, 440px"
        // A story's own cover comes from media storage — served as-is, like the
        // gallery cards do, rather than through the CF resizing loader.
        unoptimized={/^https?:\/\//.test(poster)}
        className="object-cover"
      />
      {children}

      {/* The whole poster stays clickable (a big target on a phone), but only the
          bottom strip is painted, so the art itself is never covered. */}
      <button
        type="button"
        onClick={() => setPlaying(true)}
        aria-label={title ? `${playLabel}: ${title}` : playLabel}
        className="group absolute inset-0 flex cursor-pointer items-end focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/60"
        data-testid="hero-story-play"
      >
        <span className="flex w-full items-center gap-2 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-3 pt-10">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary shadow-lg transition group-hover:scale-105">
            {/* optical centring — the triangle sits a hair right of centre */}
            <Play className="size-4 translate-x-0.5 fill-white text-white" aria-hidden />
          </span>
          <span className="min-w-0 text-left">
            <span className="block text-sm font-semibold text-white drop-shadow">{playLabel}</span>
            {title && (
              <span className="block truncate text-xs text-white/80 drop-shadow">{title}</span>
            )}
          </span>
        </span>
      </button>
    </div>
  );
}
